/**
 * SessionArtifactsPanel — the session's Documents tab.
 *
 * One place for what a session keeps beside its results: a recording, a
 * video, a transcript, or any document — as a file from your computer, a
 * link, or pasted text. Rows persist to `session_artifacts`; files live in
 * the private `session-documents` bucket (migration 818) and open through a
 * one-hour signed link. Every document shows on the session's lineage.
 *
 * Canon: Doc 21 §6.4. Roadmap D-5. Brief K §2a. The component keeps its
 * older name so its callers and the `artifacts` tab id (in URLs) stay stable.
 */

import { useState, useEffect, useMemo, useRef } from 'react'
import {
  FileText, Video, Mic, Plus, Trash2, ExternalLink,
  ChevronDown, ChevronUp, Link2, AlignLeft, Upload, Loader2,
} from 'lucide-react'
import {
  useSessionArtifactsStore,
  type SessionArtifact,
} from '../../../stores/prioritization/sessionArtifactsStore'
import { useUserStore } from '../../../stores/prioritization/userStore'
import {
  isFileArtifactType,
  type SessionArtifactType,
} from '../../../types/prioritization/sessionArtifacts'
import {
  SESSION_DOCUMENT_ACCEPT,
  describeFileSize,
  documentTypeForFile,
  getSessionDocumentUrl,
  sessionDocumentProblem,
  uploadSessionDocument,
} from '../../../lib/sessionDocuments'
import { generateUUID } from '../../../lib/supabaseSync'
import { SessionMeetingSection } from './SessionMeetingSection'

// ============================================================================
// Kinds
// ============================================================================

interface KindMeta {
  label: string
  icon: React.ComponentType<{ className?: string }>
  color: string
}

const CORAL = 'text-[var(--coral)] bg-[var(--coral-dim)] border-[var(--coral)]'
const MUSTARD = 'text-[var(--mustard)] bg-[var(--mustard-dim)] border-[var(--mustard)]'
const SLATE = 'text-[var(--text-sec)] bg-[var(--slate-dim)] border-[var(--slate)]'

const KIND_META: Record<SessionArtifactType, KindMeta> = {
  audio_file:    { label: 'Recording',     icon: Mic,       color: CORAL },
  video_file:    { label: 'Video',         icon: Video,     color: CORAL },
  document_file: { label: 'Document',      icon: FileText,  color: MUSTARD },
  ai_transcript: { label: 'Transcript',    icon: AlignLeft, color: SLATE },
  video_link:    { label: 'Video link',    icon: Video,     color: CORAL },
  document_link: { label: 'Document link', icon: Link2,     color: MUSTARD },
}

/** The kinds a person picks when adding a link or pasting text (files pick their own kind). */
const LINK_KINDS: SessionArtifactType[] = ['ai_transcript', 'video_link', 'document_link']

function formatDate(d: Date): string {
  return new Date(d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/** Open a signed link in a new tab without tripping the popup blocker (open first, point later). */
async function openStoredFile(storagePath: string, onError: (message: string) => void) {
  const tab = window.open('about:blank', '_blank')
  try {
    const url = await getSessionDocumentUrl(storagePath)
    if (tab) {
      tab.opener = null
      tab.location.href = url
    } else {
      window.location.assign(url)
    }
  } catch (err) {
    tab?.close()
    onError(err instanceof Error ? err.message : 'The file could not be opened.')
  }
}

// ============================================================================
// Document row
// ============================================================================

interface DocumentRowProps {
  artifact: SessionArtifact
  userId: string
  onDelete: (id: string) => void
}

function DocumentRow({ artifact, userId, onDelete }: DocumentRowProps) {
  const [expanded, setExpanded] = useState(false)
  const [openError, setOpenError] = useState<string | null>(null)
  const meta = KIND_META[artifact.artifactType] ?? KIND_META.document_link
  const isOwn = artifact.createdBy === userId
  const isFile = isFileArtifactType(artifact.artifactType)
  const size = isFile ? describeFileSize(artifact.sizeBytes) : ''

  return (
    <div className={`border p-3 ${meta.color}`} data-testid="session-document-row">
      <div className="flex items-start gap-3">
        <meta.icon className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm truncate">{artifact.title}</span>
            <span className="text-xs opacity-60 flex-shrink-0">{meta.label}</span>
            {size && <span className="text-xs opacity-60 flex-shrink-0">{size}</span>}
            <span className="text-xs opacity-60 flex-shrink-0">{formatDate(artifact.createdAt)}</span>
          </div>

          {artifact.notes && (
            <p className="text-xs mt-1 opacity-75">{artifact.notes}</p>
          )}

          <div className="flex items-center gap-3 mt-2 flex-wrap">
            {isFile && artifact.storagePath && (
              <button
                type="button"
                onClick={() => void openStoredFile(artifact.storagePath!, setOpenError)}
                className="inline-flex items-center gap-1 text-xs font-medium hover:underline"
              >
                <ExternalLink className="h-3 w-3" />
                Open file
              </button>
            )}

            {artifact.url && (
              <a
                href={artifact.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium hover:underline"
              >
                <ExternalLink className="h-3 w-3" />
                Open link
              </a>
            )}

            {artifact.content && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="inline-flex items-center gap-1 text-xs font-medium"
              >
                <AlignLeft className="h-3 w-3" />
                {expanded ? 'Hide' : 'Read'} the transcript
                {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </button>
            )}

            {isOwn && (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Remove this document?')) onDelete(artifact.id)
                }}
                className="ml-auto inline-flex items-center gap-1 text-xs opacity-60 hover:opacity-100"
              >
                <Trash2 className="h-3 w-3" />
                Remove
              </button>
            )}
          </div>

          {openError && (
            <p className="text-xs mt-2 text-[var(--coral)]">{openError}</p>
          )}

          {expanded && artifact.content && (
            <pre className="mt-3 text-xs whitespace-pre-wrap bg-[var(--bg-raised)]/60 rounded p-3 max-h-64 overflow-y-auto border border-current/20">
              {artifact.content}
            </pre>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Add a document
// ============================================================================

type AddMode = 'file' | 'link'

interface AddFormProps {
  sessionId: string
  workspaceId: string
  userId: string
  onAdded: () => void
  onCancel: () => void
}

function AddDocumentForm({ sessionId, workspaceId, userId, onAdded, onCancel }: AddFormProps) {
  const addArtifact = useSessionArtifactsStore((s) => s.addArtifact)
  const [mode, setMode] = useState<AddMode>('file')
  const [linkKind, setLinkKind] = useState<SessionArtifactType>('ai_transcript')
  const [file, setFile] = useState<File | null>(null)
  const [title, setTitle] = useState('')
  const [url, setUrl] = useState('')
  const [content, setContent] = useState('')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  const hasUrlInput = mode === 'link' && (linkKind === 'video_link' || linkKind === 'document_link')
  const hasContentInput = mode === 'link' && linkKind === 'ai_transcript'
  const fileKind = file ? KIND_META[documentTypeForFile(file)] : null
  const canSubmit = !busy && title.trim().length > 0 && (mode === 'link' || file !== null)

  function pickFile(next: File | null) {
    setError(null)
    setFile(next)
    if (next) {
      const problem = sessionDocumentProblem(next)
      if (problem) setError(problem)
      if (!title.trim()) setTitle(next.name)
    }
  }

  async function handleSubmit() {
    if (!canSubmit) return
    setError(null)

    if (mode === 'file' && file) {
      const problem = sessionDocumentProblem(file)
      if (problem) {
        setError(problem)
        return
      }
      setBusy(true)
      const id = generateUUID()
      try {
        const uploaded = await uploadSessionDocument({ workspaceId, sessionId, artifactId: id, file })
        addArtifact({
          id,
          sessionId,
          workspaceId,
          artifactType: uploaded.artifactType,
          title: title.trim(),
          notes: notes.trim() || undefined,
          storagePath: uploaded.storagePath,
          mimeType: uploaded.mimeType,
          sizeBytes: uploaded.sizeBytes,
          createdBy: userId || undefined,
        })
        onAdded()
      } catch (err) {
        setError(err instanceof Error ? err.message : 'The file could not be saved.')
      } finally {
        setBusy(false)
      }
      return
    }

    addArtifact({
      sessionId,
      workspaceId,
      artifactType: linkKind,
      title: title.trim(),
      url: url.trim() || undefined,
      content: content.trim() || undefined,
      notes: notes.trim() || undefined,
      createdBy: userId || undefined,
    })
    onAdded()
  }

  const modeButton = (value: AddMode, label: string, Icon: React.ComponentType<{ className?: string }>) => (
    <button
      type="button"
      onClick={() => { setMode(value); setError(null) }}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
        mode === value
          ? 'bg-[var(--text-pri)] text-[var(--surface)] border-[var(--text-pri)]'
          : 'bg-[var(--bg-raised)] text-[var(--text-sec)] border-[var(--rule)]'
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  )

  return (
    <div className="border border-[var(--rule)] bg-[var(--surface)] p-4 space-y-3" data-testid="add-document-form">
      <h4 className="text-sm font-semibold text-[var(--text-pri)]">Add a document</h4>

      {/* How */}
      <div className="flex gap-2 flex-wrap">
        {modeButton('file', 'A file from your computer', Upload)}
        {modeButton('link', 'A link, or pasted text', Link2)}
      </div>

      {/* File */}
      {mode === 'file' && (
        <div>
          <input
            ref={fileInput}
            type="file"
            accept={SESSION_DOCUMENT_ACCEPT}
            className="sr-only"
            data-testid="document-file-input"
            onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            className="w-full border border-dashed border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-4 text-sm text-left text-[var(--text-sec)] hover:border-[var(--text-ter)]"
          >
            {file ? (
              <span className="flex items-center gap-2 flex-wrap">
                {fileKind && <fileKind.icon className="h-4 w-4" />}
                <span className="font-medium text-[var(--text-pri)] truncate">{file.name}</span>
                <span className="text-xs opacity-70">{fileKind?.label} · {describeFileSize(file.size)}</span>
                <span className="text-xs underline ml-auto">Choose another</span>
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Choose a recording, a video, a transcript, or a document. Up to 500 MB.
              </span>
            )}
          </button>
        </div>
      )}

      {/* Kind, for links and pasted text */}
      {mode === 'link' && (
        <div className="flex gap-2 flex-wrap">
          {LINK_KINDS.map((kind) => {
            const meta = KIND_META[kind]
            return (
              <button
                key={kind}
                type="button"
                onClick={() => setLinkKind(kind)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  linkKind === kind
                    ? meta.color
                    : 'bg-[var(--bg-raised)] text-[var(--text-sec)] border-[var(--rule)]'
                }`}
              >
                <meta.icon className="h-3.5 w-3.5" />
                {meta.label}
              </button>
            )
          })}
        </div>
      )}

      {/* Title */}
      <div>
        <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
          Title <span className="text-[var(--coral)]">*</span>
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="e.g. Recording of the Q2 ranking session"
          autoFocus={mode === 'link'}
        />
      </div>

      {/* URL (video / document link) */}
      {hasUrlInput && (
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
            <Link2 className="inline h-3.5 w-3.5 mr-1" />
            Link
          </label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="https://..."
          />
        </div>
      )}

      {/* Transcript text */}
      {hasContentInput && (
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
            <AlignLeft className="inline h-3.5 w-3.5 mr-1" />
            Transcript
            <span className="text-[var(--text-ter)] ml-1">(paste it here, or give a link below)</span>
          </label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none font-mono"
            rows={6}
            placeholder="Paste the transcript..."
          />
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1 mt-2">
            <Link2 className="inline h-3.5 w-3.5 mr-1" />
            Link, if it is hosted somewhere
          </label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="https://..."
          />
        </div>
      )}

      {/* Notes */}
      <div>
        <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
          Notes <span className="text-[var(--text-ter)]">(optional)</span>
        </label>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="Anything the next reader should know"
        />
      </div>

      {error && (
        <p className="text-xs text-[var(--coral)]" role="alert">{error}</p>
      )}

      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="px-3 py-1.5 text-sm text-[var(--text-sec)] hover:bg-[var(--hover-tint)] disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={!canSubmit}
          className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {busy ? 'Saving the file…' : 'Add'}
        </button>
      </div>
    </div>
  )
}

// ============================================================================
// SessionArtifactsPanel
// ============================================================================

export interface SessionArtifactsPanelProps {
  sessionId: string
  workspaceId: string
  canAdd?: boolean
  className?: string
}

export function SessionArtifactsPanel({
  sessionId,
  workspaceId,
  canAdd = true,
  className = '',
}: SessionArtifactsPanelProps) {
  const userId = useUserStore((s) => s.userId) || ''
  const fetchFromSupabase = useSessionArtifactsStore((s) => s.fetchFromSupabase)
  const deleteArtifact = useSessionArtifactsStore((s) => s.deleteArtifact)
  const allArtifacts = useSessionArtifactsStore((s) => s.artifacts)

  useEffect(() => {
    if (sessionId) fetchFromSupabase()
  }, [sessionId, fetchFromSupabase])

  const documents = useMemo(
    () =>
      allArtifacts
        .filter((a) => a.sessionId === sessionId)
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
    [allArtifacts, sessionId],
  )

  const [showForm, setShowForm] = useState(false)

  return (
    <div className={`space-y-4 ${className}`} data-testid="session-documents">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-[var(--text-ter)]" />
          <span className="text-sm font-semibold text-[var(--text-sec)]">
            Documents
          </span>
          {documents.length > 0 && (
            <span className="text-xs bg-[var(--hover-tint)] text-[var(--text-sec)] rounded-full px-2 py-0.5">
              {documents.length}
            </span>
          )}
        </div>
        {canAdd && !showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-primary-600 text-white hover:bg-primary-700"
            data-testid="add-document"
          >
            <Plus className="h-3.5 w-3.5" />
            Add
          </button>
        )}
      </div>

      {/* The session's meeting: its transcript arrives here on its own (K7) */}
      {canAdd && (
        <SessionMeetingSection
          sessionId={sessionId}
          workspaceId={workspaceId}
          onDocumentArrived={() => void fetchFromSupabase()}
        />
      )}

      {/* Add form */}
      {showForm && (
        <AddDocumentForm
          sessionId={sessionId}
          workspaceId={workspaceId}
          userId={userId}
          onAdded={() => setShowForm(false)}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* List */}
      {documents.length === 0 && !showForm ? (
        <div className="text-center py-8 text-[var(--text-ter)]">
          <FileText className="h-8 w-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No documents yet.</p>
          {canAdd && (
            <p className="text-xs mt-1">
              Add the recording, the video, the transcript, or any document, so people who were not here can follow what happened.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {documents.map((a) => (
            <DocumentRow
              key={a.id}
              artifact={a}
              userId={userId}
              onDelete={deleteArtifact}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default SessionArtifactsPanel
